import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';

interface EditDisplayNameDialogProps {
  open: boolean;
  onClose: () => void;
  currentDisplayName: string;
  onSave: (newDisplayName: string) => Promise<void>;
  title?: string;
  label?: string;
}

const EditDisplayNameDialog: React.FC<EditDisplayNameDialogProps> = ({
  open,
  onClose,
  currentDisplayName,
  onSave,
  title = 'Edit Name',
  label = 'Display name',
}) => {
  const [value, setValue] = useState(currentDisplayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setValue(currentDisplayName);
      setError('');
    }
  }, [open, currentDisplayName]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Name cannot be empty');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(trimmed);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={label}
          fullWidth
          variant="outlined"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!error}
          helperText={error}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDisplayNameDialog;
